import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import "./CollectionAnalytics.css";

// Preset durations, mirroring the SocialListeningDaysDropDown widget.
// `amount` / `unit` feed moment().subtract() for the from-date calculation.
const DURATION_PRESETS = [
  { value: "yesterday", label: "Yesterday", amount: 1, unit: "days" },
  { value: "last7days", label: "Last 7 days", amount: 7, unit: "days" },
  { value: "last30days", label: "Last 30 days", amount: 30, unit: "days" },
  { value: "last3months", label: "Last 3 months", amount: 3, unit: "months" },
  { value: "last6months", label: "Last 6 months", amount: 6, unit: "months" },
  {
    value: "last12months",
    label: "Last 12 months",
    amount: 12,
    unit: "months",
  },
];

const DATE_OPTIONS = [
  { value: "all", label: "All time" },
  ...DURATION_PRESETS.map(({ value, label }) => ({ value, label })),
  { value: "custom", label: "Custom range" },
];

// Build the from/to range for a preset. `yesterday` spans that single day;
// every other preset runs from N days/months ago up to today. Mirrors the
// moment().subtract() calculation in SocialListeningDaysDropDown.
const computeRange = (value) => {
  const preset = DURATION_PRESETS.find((p) => p.value === value);
  if (!preset) return null;
  const from = moment().subtract(preset.amount, preset.unit);
  const to =
    preset.value === "yesterday" ? moment().subtract(1, "days") : moment();
  return {
    from_date: from.format("YYYY-MM-DD"),
    to_date: to.format("YYYY-MM-DD"),
  };
};

// Resolve a from/to range back to a preset. `yesterday` matches when both
// ends fall on yesterday; the rolling presets match when end === today and
// start === today − amount. Anything else is treated as custom. Mirrors the
// isSame(..., "day") matching in SocialListeningDaysDropDown.
const presetFromRange = (from_date, to_date) => {
  if (!from_date && !to_date) return "all";
  if (!from_date || !to_date) return "custom";

  const startDate = moment(from_date);
  const endDate = moment(to_date);
  const today = moment();

  const match = DURATION_PRESETS.find((preset) => {
    const expectedEnd =
      preset.value === "yesterday" ? moment().subtract(1, "days") : today;
    return (
      startDate.isSame(moment().subtract(preset.amount, preset.unit), "day") &&
      endDate.isSame(expectedEnd, "day")
    );
  });
  return match ? match.value : "custom";
};

export const formatCompact = (value) => {
  if (value == null || isNaN(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
};

// Money formatter for GMV/EMV: always renders 1 decimal of accuracy
// (e.g. $712.0, $1.5K, $2.3M) so the value reads consistently and fits
// the tile instead of overflowing with a long float.
const formatMoney = (value) => {
  if (value == null || isNaN(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(1)}`;
};

const CollectionAnalytics = ({ items = [], stats = {}, onDateChange }) => {
  const history = useHistory();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("presets");
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const popupRef = useRef(null);

  const urlSearch = location.search;
  const { dateValue, fromDate, toDate } = useMemo(() => {
    const params = new URLSearchParams(urlSearch);
    const f = params.get("from_date") || "";
    const t = params.get("to_date") || "";
    return {
      dateValue: presetFromRange(f, t),
      fromDate: f,
      toDate: t,
    };
  }, [urlSearch]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleToggle = () => {
    if (!open) {
      setDraftFrom(fromDate);
      setDraftTo(toDate);
      setView(dateValue === "custom" ? "custom" : "presets");
    }
    setOpen((v) => !v);
  };

  const derived = useMemo(() => {
    const posts = items.length;
    const creators = new Set(
      items.map((item) => item?.creator?.username).filter(Boolean),
    ).size;
    return { posts, creators };
  }, [items]);

  const tiles = [
    {
      key: "posts",
      value: stats.posts ?? derived.posts ?? 0,
      label: "POSTS",
      hint: "across platforms",
    },
    {
      key: "creators",
      value: stats.creators ?? derived.creators ?? 0,
      label: "CREATORS",
      hint: "unique handles",
    },
    {
      key: "impressions",
      value: stats.impressions ?? formatCompact(stats.impressionsRaw),
      label: "IMPRESSIONS",
      hint: "estimated reach",
    },
    {
      key: "engagement",
      value: stats.engagement ?? formatCompact(stats.engagementRaw),
      label: "ENGAGEMENT",
      hint: "likes + comments + shares",
    },
    // {
    //   key: "emv",
    //   value: stats.emv ?? formatMoney(stats.emvRaw),
    //   label: "EMV",
    //   hint: "earned media value",
    // },
  ];

  const activeLabel = useMemo(() => {
    if (dateValue === "custom") {
      if (fromDate && toDate) return `${fromDate} → ${toDate}`;
      return "Custom range";
    }
    return (
      DATE_OPTIONS.find((opt) => opt.value === dateValue)?.label || "All time"
    );
  }, [dateValue, fromDate, toDate]);

  const writeUrl = (params) => {
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  const handleSelectPreset = (value) => {
    if (value === "custom") {
      setDraftFrom(fromDate);
      setDraftTo(toDate);
      setView("custom");
      return;
    }
    const params = new URLSearchParams(location.search);
    const range = computeRange(value);
    if (range) {
      params.set("from_date", range.from_date);
      params.set("to_date", range.to_date);
    } else {
      params.delete("from_date");
      params.delete("to_date");
    }
    writeUrl(params);
    setOpen(false);
    onDateChange?.(value);
  };

  const handleApplyCustom = () => {
    if (!draftFrom || !draftTo) return;
    const params = new URLSearchParams(location.search);
    params.set("from_date", draftFrom);
    params.set("to_date", draftTo);
    writeUrl(params);
    setOpen(false);
    onDateChange?.("custom");
  };

  const handleClearCustom = () => {
    setDraftFrom("");
    setDraftTo("");
    const params = new URLSearchParams(location.search);
    params.delete("from_date");
    params.delete("to_date");
    writeUrl(params);
    setOpen(false);
    onDateChange?.("all");
  };

  return (
    <section className="collection-analytics" aria-label="Collection analytics">
      <div className="ca-toolbar">
        <div
          className={`ca-date-select ${open ? "is-open" : ""}`}
          ref={popupRef}
        >
          <button
            type="button"
            className="ca-date-button"
            onClick={handleToggle}
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <span className="ca-date-kicker">DATE</span>
            <span className="ca-date-value">{activeLabel}</span>
            <svg
              className="ca-date-chevron"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {open && view === "presets" && (
            <ul className="ca-date-menu" role="listbox">
              {DATE_OPTIONS.map((opt) => (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === dateValue}
                  className={`ca-date-option ${
                    opt.value === dateValue ? "is-active" : ""
                  }`}
                  onClick={() => handleSelectPreset(opt.value)}
                >
                  {opt.label}
                </li>
              ))}
            </ul>
          )}
          {open && view === "custom" && (
            <div
              className="ca-custom-popup"
              role="dialog"
              aria-label="Custom date range"
            >
              <div className="ca-custom-header">
                <button
                  type="button"
                  className="ca-custom-back"
                  onClick={() => setView("presets")}
                  aria-label="Back to presets"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M7.5 3L4.5 6L7.5 9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <span className="ca-custom-title">Custom range</span>
              </div>
              <div className="ca-custom-fields">
                <label className="ca-custom-field">
                  <span className="ca-custom-label">From</span>
                  <input
                    type="date"
                    className="ca-custom-input"
                    value={draftFrom}
                    max={draftTo || undefined}
                    onChange={(e) => setDraftFrom(e.target.value)}
                  />
                </label>
                <label className="ca-custom-field">
                  <span className="ca-custom-label">To</span>
                  <input
                    type="date"
                    className="ca-custom-input"
                    value={draftTo}
                    min={draftFrom || undefined}
                    onChange={(e) => setDraftTo(e.target.value)}
                  />
                </label>
              </div>
              <div className="ca-custom-actions">
                <button
                  type="button"
                  className="ca-custom-clear"
                  onClick={handleClearCustom}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="ca-custom-apply"
                  onClick={handleApplyCustom}
                  disabled={!draftFrom || !draftTo}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="ca-count">
          Showing {derived.posts} post{derived.posts === 1 ? "" : "s"}
        </div>
      </div>

      <div className="ca-card" role="list">
        {tiles.map((tile, idx) => (
          <div
            key={tile.key}
            className="ca-tile"
            role="listitem"
            style={{ "--idx": idx }}
          >
            <div className="ca-tile-value">{tile.value}</div>
            <div className="ca-tile-label">{tile.label}</div>
            <div className="ca-tile-hint">{tile.hint}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CollectionAnalytics;
